import { useEffect, useRef } from 'react'
import { App, Button } from 'antd'
import dayjs from 'dayjs'
import { Printer } from 'lucide-react'
import { useReactToPrint } from 'react-to-print'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { ContractLexicalEditor, type ContractLexicalContent } from '@/features/contract/index.ts'

export type RentalBatchPrintDocument = {
  id: number
  title: string
  content: ContractLexicalContent
}

const pageStyle = `
  @page { size: A4; margin: 20mm; }
  @media print {
    body { margin: 0; background: white; }
    .contract-editor-content { color: #000; }
    .contract-editor-content table { width: 100%; border-collapse: collapse; }
    .contract-editor-content tr { break-inside: avoid; }
  }
`

export function RentalBatchContractPrintPreview({ documents }: { documents: RentalBatchPrintDocument[] }) {
  const { message } = App.useApp()
  const contentRef = useRef<HTMLDivElement>(null)
  const printed = useRef(false)
  const print = useReactToPrint({
    contentRef,
    documentTitle: `订单合同-${dayjs().format('YYYY-MM-DD')}`,
    pageStyle,
    onPrintError: (_, error) => void message.error(error.message || '打开打印窗口失败')
  })

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (printed.current) {
        return
      }
      printed.current = true
      void print()
    }, 500)
    return () => window.clearTimeout(timer)
  }, [print])

  return (
    <div className="bg-page flex h-dvh min-h-0 flex-col overflow-hidden">
      <WorkspaceHeader className="h-auto min-h-15 flex-wrap overflow-visible py-3 print:hidden">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="m-0 text-base font-semibold">合同打印预览</h1>
            <span className="text-content-muted text-xs">已保存 · 共 {documents.length} 份合同</span>
          </div>
        </div>
        <Button
          disabled={!documents.length}
          icon={<Printer aria-hidden size={16} />}
          onClick={() => void print()}
          type="primary"
        >
          打印全部合同
        </Button>
      </WorkspaceHeader>
      <main className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 overflow-hidden p-3 lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-1">
        <nav
          aria-label="打印合同目录"
          className="border-border bg-surface max-h-40 overflow-auto rounded-xl border p-3 lg:max-h-none"
        >
          <h2 className="m-0 text-sm font-semibold">打印清单</h2>
          <p className="text-content-muted mt-1 mb-3 text-xs">点击合同定位正文，打印时每份合同另起一页。</p>
          <div className="flex flex-col gap-2">
            {documents.map((document, index) => (
              <a
                key={document.id}
                href={`#print-contract-${document.id}`}
                className="border-border text-content hover:border-accent/50 hover:bg-accent/10 focus-visible:outline-accent rounded-lg border p-3 text-sm focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <span className="text-content-muted mb-1 block text-xs">合同 {index + 1}</span>
                <span className="break-words">{document.title}</span>
              </a>
            ))}
          </div>
        </nav>
        <section
          aria-label="合同打印预览"
          className="border-border bg-surface-muted min-h-0 min-w-0 overflow-auto rounded-xl border"
        >
          <div className="border-border bg-surface text-content-muted border-b px-4 py-3 text-xs">
            A4 纵向 · 页边距 20mm · 请核对合同内容后打印
          </div>
          <div ref={contentRef} className="p-3 sm:p-6 print:p-0">
            {documents.map((document, index) => (
              <article
                key={document.id}
                id={`print-contract-${document.id}`}
                className="mx-auto mb-6 w-full max-w-[51.6063rem] scroll-mt-4 last:mb-0 print:mb-0 print:max-w-none print:break-after-page print:last:break-after-auto"
              >
                <h2 className="text-content-muted mt-0 mb-2 text-sm font-medium break-words print:hidden">
                  {index + 1}. {document.title}
                </h2>
                <ContractLexicalEditor initialValue={document.content} readOnly />
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}
